import {
  NodeStatusEvent,
  NodeEvent,
  NodeEventType,
  TerminationEvent,
  TerminationStatusCode,
  WorkflowEventTypes,
} from '@/types/workflow-types';
import { deriveNodeStatusFromEvents } from '@/pages/workflow/helper/derive-node-status';

/**
 * Helper functions for determining workflow status bar state
 *
 * These functions encapsulate the complex logic for determining what status
 * the workflow status bar should display based on various event streams,
 * connection state, and execution state.
 */

/**
 * Detects workflow-level and node-level errors in the event stream
 *
 * For node-level events, delegates to {@link deriveNodeStatusFromEvents}
 * which correctly handles retry superseding: a failure followed by
 * RETRY_SCHEDULED, a new BEGIN, or EXECUTION_SUCCESSFUL is not an error.
 *
 * For workflow-level events (execution_result), checks for success: false.
 *
 * @param nodeStatuses - Record of node IDs to their event arrays
 * @returns true if any errors are detected, false otherwise
 */
export const detectWorkflowErrors = (
  nodeStatuses: Record<string, NodeStatusEvent[]>,
): boolean => {
  return Object.entries(nodeStatuses).some(([key, events]) => {
    // Workflow-level failures (ImportError, initialization errors, etc.)
    // stored under the 'execution_result' synthetic key
    if (key === 'execution_result') {
      return events.some(
        (event) =>
          event.event_type === WorkflowEventTypes.EXECUTION_RESULT &&
          'data' in event &&
          event.data &&
          typeof event.data === 'object' &&
          'success' in event.data &&
          event.data.success === false,
      );
    }

    // Node-level failures: use derived status which handles retry superseding
    return deriveNodeStatusFromEvents(events).hasError;
  });
};

/**
 * Checks if any nodes have successfully completed execution
 *
 * @param nodeStatuses - Record of node IDs to their event arrays
 * @returns true if any node has EXECUTION_SUCCESSFUL event, false otherwise
 */
/**
 * Detects if the workflow was cancelled by the user
 *
 * Checks for execution_result events with success: false where
 * the error type is 'cancelled' (user-initiated stop)
 *
 * @param nodeStatuses - Record of node IDs to their event arrays
 * @returns true if the workflow was cancelled by user, false otherwise
 */
export const isUserCancellation = (
  nodeStatuses: Record<string, NodeStatusEvent[]>,
): boolean => {
  return Object.values(nodeStatuses).some((events) =>
    events.some((event) => {
      if (
        event.event_type === WorkflowEventTypes.EXECUTION_RESULT &&
        'data' in event &&
        event.data &&
        typeof event.data === 'object' &&
        'success' in event.data &&
        event.data.success === false
      ) {
        const data = event.data as {
          success: boolean;
          execution_summary?: {
            errors?: Array<{ type?: string; error?: string }>;
          };
        };
        const errors = data.execution_summary?.errors || [];
        return errors.some((err) => err.type === 'cancelled');
      }
      return false;
    }),
  );
};

/**
 * Terminal classification for the canvas run-status pill.
 *
 * Returns 'failed' only for a genuine failure — a node/workflow-level error or
 * an ERROR termination. A user-initiated stop (STOPPED termination or a
 * 'cancelled' execution_result) is never 'failed'; it reads as 'success' so the
 * pill stays available as a dismissable notification without mislabelling.
 */
export const classifyTerminalRun = (
  nodeStatuses: Record<string, NodeStatusEvent[]>,
): 'success' | 'failed' => {
  const workflowEvents = nodeStatuses['workflow_events'] || [];
  const termination = workflowEvents.find(
    (event) => event.event_type === WorkflowEventTypes.TERMINATION,
  ) as TerminationEvent | undefined;

  const wasStopped =
    termination?.status_code === TerminationStatusCode.STOPPED ||
    isUserCancellation(nodeStatuses);
  if (wasStopped) return 'success';

  const failed =
    detectWorkflowErrors(nodeStatuses) ||
    termination?.status_code === TerminationStatusCode.ERROR;
  return failed ? 'failed' : 'success';
};

export const hasExecutionSuccessEvents = (
  nodeStatuses: Record<string, NodeStatusEvent[]>,
): boolean => {
  return Object.values(nodeStatuses).some((events) =>
    events.some((event) => {
      if (
        'node_id' in event &&
        'node_display_name' in event &&
        'event_type' in event
      ) {
        const nodeEvent = event as NodeEvent;
        return nodeEvent.event_type === NodeEventType.EXECUTION_SUCCESSFUL;
      }
      return false;
    }),
  );
};

export type WsReconnectStatus = {
  isReconnecting: boolean;
  enabled?: boolean;
  attempts?: number;
  maxAttempts?: number;
};

/**
 * True while the WebSocket service is still retrying (including the silent
 * first attempt where `isReconnecting` stays false in the singleton).
 */
export const isReconnectAttemptsInProgress = (
  reconnectStatus: WsReconnectStatus,
): boolean => {
  if (reconnectStatus.isReconnecting) {
    return true;
  }
  const enabled = reconnectStatus.enabled ?? false;
  const maxAttempts = reconnectStatus.maxAttempts ?? 0;
  const attempts = reconnectStatus.attempts ?? 0;
  // attempts === 0 is the normal pre-disconnect state after markWorkflowStarted(),
  // not an active retry — require attempts > 0 to avoid a Reconnecting flash on Play.
  return enabled && maxAttempts > 0 && attempts > 0 && attempts < maxAttempts;
};

/**
 * Determines if the workflow should show "Reconnecting" status
 *
 * The workflow is reconnecting when:
 * - Workflow execution is in progress
 * - WebSocket is disconnected
 * - Reconnection attempts are in progress (including silent first retry)
 *
 * @param isExecutingWorkflow - Whether a workflow execution is in progress
 * @param isConnected - Whether WebSocket is connected
 * @param reconnectStatus - Reconnection status from WebSocket service
 * @returns true if should show reconnecting status
 */
export const shouldShowReconnecting = (
  isExecutingWorkflow: boolean,
  isConnected: boolean,
  reconnectStatus: WsReconnectStatus,
): boolean => {
  return (
    isExecutingWorkflow &&
    !isConnected &&
    isReconnectAttemptsInProgress(reconnectStatus)
  );
};

/**
 * Determines if the workflow should show "Running" status
 *
 * The workflow is running when:
 * - Workflow execution is in progress
 * - WebSocket is connected
 * - No connection errors present
 * - Workflow was not manually stopped
 *
 * @param isExecutingWorkflow - Whether a workflow execution is in progress
 * @param isConnected - Whether WebSocket is connected
 * @param connectionError - Any connection error message
 * @param wasManuallyStopped - Whether user clicked stop button
 * @returns true if should show running status
 */
export const shouldShowRunning = (
  isExecutingWorkflow: boolean,
  isConnected: boolean,
  connectionError: string | null,
  wasManuallyStopped: boolean,
): boolean => {
  return (
    isExecutingWorkflow &&
    isConnected &&
    !connectionError &&
    !wasManuallyStopped
  );
};

/**
 * Determines if the workflow should show "Completed" status
 *
 * The workflow is completed when:
 * - Workflow execution has finished
 * - No errors detected in any node
 * - Termination was not an error or STOPPED
 * - No connection errors
 * - Has an explicit completion signal (termination event OR execution result)
 * - Was NOT cancelled by the user (cancellations go to IDLE state)
 *
 * @param isExecutingWorkflow - Whether a workflow execution is in progress
 * @param hasError - Whether any errors were detected
 * @param terminationIsError - Whether termination event indicates error
 * @param connectionError - Any connection error message
 * @param hasExecutionSuccessful - Whether any nodes completed successfully
 * @param terminationEvent - Termination event if present
 * @param hasExecutionResult - Whether execution result event is present
 * @param wasCancelledByUser - Whether the workflow was cancelled by the user
 * @returns true if should show completed status
 */
export const shouldShowCompleted = (
  isExecutingWorkflow: boolean,
  hasError: boolean,
  terminationIsError: boolean,
  connectionError: string | null,
  _hasExecutionSuccessful: boolean,
  terminationEvent: TerminationEvent | undefined,
  hasExecutionResult: boolean,
  wasCancelledByUser: boolean = false,
): boolean => {
  if (wasCancelledByUser) {
    return false;
  }

  if (terminationEvent?.status_code === TerminationStatusCode.STOPPED) {
    return false;
  }

  return (
    !isExecutingWorkflow &&
    !hasError &&
    !terminationIsError &&
    !connectionError &&
    (!!terminationEvent || hasExecutionResult)
  );
};

/**
 * Determines if the workflow should show "Failed" status.
 *
 * Only returns true when the BACKEND has confirmed failure:
 * - Workflow execution error detected (execution_result.success === false)
 * - Termination event with error status code
 *
 * WebSocket disconnection alone does NOT trigger failure — it routes
 * through {@link shouldCheckRunStatus} instead, which polls the backend.
 */
export const shouldShowFailed = (
  _isConnected: boolean,
  isExecutingWorkflow: boolean,
  _reconnectStatus: { isReconnecting: boolean },
  _connectionError: string | null,
  hasError: boolean,
  terminationIsError: boolean,
  _hasEverConnected: boolean,
  wasCancelledByUser: boolean = false,
): boolean => {
  if (wasCancelledByUser) {
    return false;
  }

  return (hasError && !isExecutingWorkflow) || terminationIsError;
};

/**
 * Determines if the workflow should enter the CHECKING_STATUS state.
 *
 * This happens when:
 * - WebSocket reconnection is exhausted during an active workflow, OR
 * - An explicit connection error occurred during workflow execution
 *
 * The frontend should poll the backend API to determine the actual
 * run status rather than assuming failure.
 */
export const shouldCheckRunStatus = (
  isConnected: boolean,
  isExecutingWorkflow: boolean,
  reconnectStatus: WsReconnectStatus,
  hasEverConnected: boolean,
  connectionError?: string | null,
): boolean => {
  if (!isExecutingWorkflow || isReconnectAttemptsInProgress(reconnectStatus)) {
    return false;
  }

  // Reconnection exhausted after having been connected
  if (!isConnected && hasEverConnected) {
    return true;
  }

  // Explicit connection error during execution
  if (connectionError) {
    return true;
  }

  return false;
};
